import {Component, OnDestroy, OnInit} from '@angular/core';
import {StateService} from "../../services/state.service";
import {Institution, InstService} from "../../services/inst.service";
import {ValidatedForm} from "../../forms/validated-form";
import {FormBuilder, Validators} from "@angular/forms";
import {Constants} from "../../util/constants";
import {STATES} from '../../util/us-states';
import {Router} from "@angular/router";
import {finalize} from "rxjs/operators";
import {UtilService} from "../../services/util.service";

const PROMO_PATTERN = /^[A-Z0-9]{3,}$/i;
const PROMO_RULES = "Must have at least 3 letters and/or digits";
const SIMPLE_FIELDS =
    ['name', 'type', 'contactName', 'contactRole', 'contactEmail', 'contactPhone', 'notificationEmail', 'promoPrefix', 'notes'];
const ADDRESS_FIELDS = ['street', 'street_2', 'city', 'state', 'zip'];


@Component({
    selector: 'app-institution',
    templateUrl: './institution.component.html',
    styleUrls: ['./institution.component.css']
})
export class InstitutionComponent extends ValidatedForm implements OnInit, OnDestroy {

    readonly TYPES: string[] = ['Educational', 'Government', 'Corporate', 'Nonprofit', 'Other'];
    readonly US_STATES = STATES;

    updating: boolean;
    inst: Institution;
    action: string;
    
    constructor(protected fb: FormBuilder, 
                private instService: InstService,
                private router: Router,
                private utilService: UtilService,
                private state: StateService) {
        super(fb);
        this.inst = this.state.get('institution');
        this.action = this.inst? "Edit" : "Create New";
        this.initForm({
            name: { value: this.inst?.name || '', validators: [Validators.required], messages: {required: Constants.ERR_REQUIRED}},
            type: { value: this.inst?.type || '', validators: [Validators.required], messages: {required: Constants.ERR_REQUIRED}},
            street: { value: this.inst?.address.street || '', validators: [Validators.required], messages: {required: Constants.ERR_REQUIRED}},
            street_2: { value: this.inst?.address.street_2 || ''},
            city: { value: this.inst?.address.city || '', validators: [Validators.required], messages: {required: Constants.ERR_REQUIRED}},
            state: { value: this.inst?.address.state || '', validators: [Validators.required], messages: {required: Constants.ERR_REQUIRED}},
            zip: { 
                value: this.inst?.address.zip || '',
                validators: [Validators.required, Validators.pattern(Constants.US_ZIP_REGEX)],
                messages: {required: Constants.ERR_REQUIRED, pattern: Constants.ERR_INVALID_ZIP}
            },
            contactName: {
                value: this.inst?.contactName || '',
                validators: [Validators.required],
                messages: {required: Constants.ERR_REQUIRED}
            },
            contactRole: { value: this.inst?.contactRole || ''},
            contactEmail: {
                value: this.inst?.contactEmail || '',
                validators: [Validators.required, Validators.pattern(Constants.EMAIl_REGEX)],
                messages: { required: Constants.ERR_REQUIRED, pattern: Constants.ERR_INVALID_EMAIL }
            },
            contactPhone: {
                value: this.inst?.contactPhone || '',
                validators: [Validators.pattern(Constants.US_PHONE_REGEX)],
                messages: { pattern: Constants.ERR_INVALID_PHONE }
            },
            notificationEmail: {
                value: this.inst?.notificationEmail || '',
                validators: [Validators.pattern(Constants.EMAIl_REGEX)],
                messages: { pattern: Constants.ERR_INVALID_EMAIL }
            },
            promoPrefix: {
                value: this.inst?.promoPrefix || '',
                validators: [Validators.required, Validators.pattern(PROMO_PATTERN)],
                messages: { required: Constants.ERR_REQUIRED, pattern: PROMO_RULES}
            },
            notes: { value: this.inst?.notes || ''}
        });
    }

    ngOnInit() {
    }

    ngOnDestroy() {
        this.state.clear('institution');
    }

    submit() {
        if (this.inst)
            this.updateInst();
        else
            this.addInst()
    }

    private updateInst() {
        const controls = this.form.controls;
        // Fill new Institution instance that has the same ID as current one, and all the properties of one,
        // but with their values null unless changed by the user
        let changedInst:any = { id: this.inst.id };
        SIMPLE_FIELDS.forEach((key) => {
            changedInst[key] = controls[key]?.dirty? this.fields[key]?.value : null;
        });
        // Address property needs to be updated if any address field has changed
        if (ADDRESS_FIELDS.some((key) => controls[key]?.dirty)) {
            changedInst.address = {
                street: this.fields.street.value,
                street_2: this.fields.street_2.value || null,
                city: this.fields.city.value,
                state: this.fields.state.value,
                zip: this.fields.zip.value
            };
        } else
            changedInst.address = null;

        this.updating = true;
        this.instService.update(changedInst as Institution).pipe(
            finalize(() => { this.updating=false })
        ).subscribe((info) => {
            this.goBack();
        }, this.utilService.getErrorHandler("Error updating institution"));
    }

    private addInst() {
        const inst: Institution = {
            name: this.fields.name.value,
            type: this.fields.type.value,
            address: {
                street: this.fields.street.value,
                street_2: this.fields.street_2.value || null,
                city: this.fields.city.value,
                state: this.fields.state.value,
                zip: this.fields.zip.value
            },
            contactName: this.fields.contactName.value,
            contactRole: this.fields.contactRole.value || null,
            contactEmail: this.fields.contactEmail.value,
            contactPhone: this.fields.contactPhone.value || null,
            notificationEmail: this.fields.notificationEmail.value || null,
            promoPrefix: this.fields.promoPrefix.value,
            notes: this.fields.notes.value || null
        };
        this.updating = true;
        this.instService.add(inst).pipe(
            finalize(() => { this.updating=false })
        ).subscribe((info) => {
            this.goBack();
        }, this.utilService.getErrorHandler("Error creating institution"));
    }

    cancel() {
        this.goBack();
    }

    canSave(): boolean {
        // We can save if not waiting and all form fields are valid.
        // Furthermore, if this is an update the form needs to have some change.
        return !this.updating && this.form.valid && (!this.inst || this.form.dirty);
    }

    private goBack() {
        this.router.navigateByUrl('/institutions').then();
    }
}
